import {EMAIL_VERIFICATION_ROUTE} from "@/lib/EmailVerificationGate";
import {shouldKickOnboardingToLogin} from "@/lib/OnboardingRouteDecision";

export const AUTHENTICATED_HOME_ROUTE = "__authenticated_home__";
export const SESSION_ROUTE_LOOP_THRESHOLD = 8;

export type SessionRouteKind = "wait" | "navigate" | "recover" | "render" | "handoff";

export type SessionRouteReason =
    | "auth_loading"
    | "auth_context_lag"
    | "auth_degraded"
    | "loop_breaker"
    | "signed_out"
    | "email_unverified"
    | "desktop_handoff"
    | "claim"
    | "return_to"
    | "onboarding_complete"
    | "onboarding_complete_cached"
    | "onboarding_intent"
    | "onboarding_connect"
    | "onboarding_player"
    | "onboarding_pricing"
    | "snapshot_unknown"
    | "already_on_route";

export type SessionRouteMode = "entry" | "gate" | "onboard";

export interface SessionOnboardingState {
    pricing?: boolean;
    connect?: boolean;
    player?: boolean;
    editScreen?: boolean;
    intents?: Record<string, boolean>;
}

export interface SessionPermissionsState {
    admin: boolean;
    betaProgram: boolean;
    dubbing: boolean;
    upload: boolean;
}

export interface SessionSnapshot {
    emailVerified?: boolean;
    onboarding?: SessionOnboardingState;
    permissions?: SessionPermissionsState;
    signupIntent?: string | null;
    pendingAuthReturnTo?: string | null;
    pendingAuthReturnToConsumed?: boolean;
    pendingAuthReturnToCreatedAtMs?: number | null;
}

export type SessionRouteDecision = {
    kind: SessionRouteKind;
    route?: string;
    reason: SessionRouteReason;
};

const ONBOARDING_PATHS = new Set([
    "/onboard",
    "/onboard-pricing",
    "/setup-channel",
    "/setup-channel-languages",
    "/setup-channel-languages-add",
    "/setup-branding",
]);

/** Pages that can render a signed-out UI. `/` is only a spinner splash. */
const AUTH_FORM_PATHS = new Set(["/login", "/signup"]);
/** Login, signup, and `/` — bounce completed sessions into the app. */
const AUTH_ENTRY_PATHS = new Set(["/login", "/signup", "/"]);

export function getIncompleteOnboardingRoute(
    onboarding?: SessionOnboardingState | null
): string | null {
    if (onboarding?.pricing === true) {
        return null;
    }

    const intents = onboarding?.intents;
    const hasSelectedIntent = Boolean(intents && Object.keys(intents).length > 0);
    if (!hasSelectedIntent) {
        return "/onboard";
    }
    if (onboarding?.connect) {
        return "/setup-channel";
    }
    if (onboarding?.player) {
        return "/setup-branding";
    }
    return "/onboard-pricing";
}

export function snapshotFromProfile(profile?: {
    onboardPricing?: boolean;
    onboardConnect?: boolean;
    onboardPlayer?: boolean;
    onboardEditScreen?: boolean;
    userIntents?: Record<string, boolean>;
    signupIntent?: string;
    pendingAuthReturnTo?: string;
    pendingAuthReturnToConsumedAt?: Date;
    pendingAuthReturnToCreatedAt?: Date;
} | null): SessionSnapshot | null {
    if (!profile) {
        return null;
    }
    return {
        onboarding: {
            pricing: profile.onboardPricing,
            connect: profile.onboardConnect,
            player: profile.onboardPlayer,
            editScreen: profile.onboardEditScreen,
            intents: profile.userIntents,
        },
        signupIntent: profile.signupIntent ?? null,
        pendingAuthReturnTo: profile.pendingAuthReturnTo ?? null,
        pendingAuthReturnToConsumed: Boolean(profile.pendingAuthReturnToConsumedAt),
        pendingAuthReturnToCreatedAtMs: profile.pendingAuthReturnToCreatedAt
            ? profile.pendingAuthReturnToCreatedAt.getTime()
            : null,
    };
}

function normalizePath(path?: string): string {
    if (!path) {
        return "";
    }
    const withoutQuery = path.split("?")[0] || "";
    if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
        return withoutQuery.slice(0, -1);
    }
    return withoutQuery;
}

function alreadyOn(currentPath: string | undefined, route: string): boolean {
    return normalizePath(currentPath) === normalizePath(route);
}

function onboardingReason(route: string): SessionRouteReason {
    if (route === "/setup-channel") {
        return "onboarding_connect";
    }
    if (route === "/setup-branding") {
        return "onboarding_player";
    }
    if (route === "/onboard-pricing") {
        return "onboarding_pricing";
    }
    return "onboarding_intent";
}

export function resolveSessionRoute(input: {
    authLoading: boolean;
    authDegraded?: boolean;
    contextUser: {uid?: string} | null | undefined;
    firebaseUser: {uid?: string} | null;
    requiresEmailVerification?: boolean;
    snapshot?: SessionSnapshot | null;
    snapshotFailed?: boolean;
    onboardingCompleteCached?: boolean;
    returnTo?: string | null;
    claimRoute?: string | null;
    isDesktopHandoff?: boolean;
    currentPath?: string;
    loopCount?: number;
    loopThreshold?: number;
    mode?: SessionRouteMode;
}): SessionRouteDecision {
    const mode = input.mode || "entry";
    const loopThreshold = input.loopThreshold ?? SESSION_ROUTE_LOOP_THRESHOLD;
    const currentPath = normalizePath(input.currentPath);

    if ((input.loopCount ?? 0) >= loopThreshold) {
        return {kind: "recover", reason: "loop_breaker"};
    }

    if (input.authLoading) {
        return {kind: "wait", reason: "auth_loading"};
    }

    if (input.contextUser === undefined) {
        return {kind: "wait", reason: "auth_context_lag"};
    }

    if (!input.contextUser && input.firebaseUser) {
        return {kind: "wait", reason: "auth_context_lag"};
    }

    const signedOut = shouldKickOnboardingToLogin({
        loading: input.authLoading,
        contextUser: input.contextUser,
        firebaseCurrentUser: input.firebaseUser,
    });

    if (signedOut) {
        if (input.authDegraded) {
            return {kind: "recover", reason: "auth_degraded"};
        }
        if (AUTH_FORM_PATHS.has(currentPath) || currentPath === "/email-verification") {
            return {kind: "render", reason: "signed_out"};
        }
        return {kind: "navigate", route: "/login", reason: "signed_out"};
    }

    if (input.requiresEmailVerification) {
        if (alreadyOn(currentPath, EMAIL_VERIFICATION_ROUTE)) {
            return {kind: "render", reason: "email_unverified"};
        }
        return {kind: "navigate", route: EMAIL_VERIFICATION_ROUTE, reason: "email_unverified"};
    }

    if (input.isDesktopHandoff) {
        const desktopComplete = input.snapshot?.onboarding?.pricing === true
            || Boolean(input.onboardingCompleteCached);
        if (desktopComplete) {
            return {kind: "handoff", reason: "desktop_handoff"};
        }
        if (input.snapshot) {
            const onboardRoute = getIncompleteOnboardingRoute(input.snapshot.onboarding);
            if (onboardRoute) {
                if (mode === "onboard" && ONBOARDING_PATHS.has(currentPath)) {
                    return {kind: "render", reason: "already_on_route"};
                }
                if (alreadyOn(currentPath, onboardRoute)) {
                    return {kind: "render", reason: "already_on_route"};
                }
                return {kind: "navigate", route: onboardRoute, reason: onboardingReason(onboardRoute)};
            }
        }
    }

    if (input.claimRoute) {
        return {kind: "navigate", route: input.claimRoute, reason: "claim"};
    }

    if (input.returnTo) {
        return {kind: "navigate", route: input.returnTo, reason: "return_to"};
    }

    const completedFromSnapshot = input.snapshot?.onboarding?.pricing === true;
    const completedFromCache = Boolean(input.onboardingCompleteCached);
    if (completedFromSnapshot || completedFromCache) {
        const reason = completedFromSnapshot ? "onboarding_complete" : "onboarding_complete_cached";
        if (mode === "gate") {
            return {kind: "render", reason};
        }
        if (
            ONBOARDING_PATHS.has(currentPath)
            || AUTH_ENTRY_PATHS.has(currentPath)
            || currentPath === EMAIL_VERIFICATION_ROUTE
        ) {
            return {kind: "navigate", route: AUTHENTICATED_HOME_ROUTE, reason};
        }
        return {kind: "render", reason};
    }

    if (input.snapshot) {
        const onboardRoute = getIncompleteOnboardingRoute(input.snapshot.onboarding);
        if (!onboardRoute) {
            if (mode === "gate") {
                return {kind: "render", reason: "onboarding_complete"};
            }
            return {kind: "navigate", route: AUTHENTICATED_HOME_ROUTE, reason: "onboarding_complete"};
        }
        if (mode === "onboard" && ONBOARDING_PATHS.has(currentPath)) {
            return {kind: "render", reason: "already_on_route"};
        }
        if (alreadyOn(currentPath, onboardRoute)) {
            return {kind: "render", reason: "already_on_route"};
        }
        return {kind: "navigate", route: onboardRoute, reason: onboardingReason(onboardRoute)};
    }

    if (mode === "gate") {
        return {kind: "render", reason: "snapshot_unknown"};
    }
    return {kind: "recover", reason: "snapshot_unknown"};
}
